import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2, ChevronRight, ChevronDown } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { APPLICATION_PRESETS, DEFAULT_EQUIPMENT } from '../../constants';
import { isEquipmentNameDuplicate, hasEquipmentWarning, generateKey } from '../../utils';
import type { PreviewEquipment, ValidationError, ApplicationType } from '../../types';

interface EquipmentTabProps {
  equipment: PreviewEquipment[];
  onChange: (equipment: PreviewEquipment[]) => void;
  errors: ValidationError[];
  disabled?: boolean;
}

export function EquipmentTab({
  equipment,
  onChange,
  errors,
  disabled = false,
}: EquipmentTabProps) {
  const { t } = useTranslation('customer-onboarding');
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const toggleExpand = (key: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleAdd = () => {
    onChange([...equipment, { ...DEFAULT_EQUIPMENT, _key: generateKey() }]);
  };

  const handleRemove = (index: number) => {
    const removedKey = equipment[index]._key;
    onChange(equipment.filter((_, i) => i !== index));
    setExpandedRows((prev) => {
      const next = new Set(prev);
      next.delete(removedKey);
      return next;
    });
  };

  const handleChange = (
    index: number,
    field: keyof PreviewEquipment,
    value: string | number | null
  ) => {
    const updated = [...equipment];
    updated[index] = { ...updated[index], [field]: value };

    // When application changes, update thresholds to preset values
    if (field === 'application') {
      const preset = APPLICATION_PRESETS[value as ApplicationType];
      if (preset && value !== 'custom') {
        updated[index].thresholdMin = preset.min;
        updated[index].thresholdMax = preset.max;
      }
    }

    onChange(updated);
  };

  const getRowErrors = (rowIndex: number) => {
    return errors.filter(
      (e) => e.entity === 'equipment' && e.row === rowIndex + 1
    );
  };

  const hasDetails = (item: PreviewEquipment) => {
    return !!(
      item.sku ||
      item.calibrationCertificate ||
      item.installationDate ||
      item.installationCertificate ||
      item.invoiceNumber ||
      item.invoiceDate ||
      item.comments
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {t('equipment.count', { count: equipment.length })}
        </p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleAdd}
          disabled={disabled}
        >
          <Plus className="mr-2 h-4 w-4" />
          {t('equipment.addEquipment')}
        </Button>
      </div>

      <div className="overflow-x-auto rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[40px]" />
              <TableHead className="w-[50px]">#</TableHead>
              <TableHead className="min-w-[120px]">{t('equipment.columns.building')}</TableHead>
              <TableHead className="min-w-[100px]">{t('equipment.columns.room')}</TableHead>
              <TableHead className="min-w-[180px]">
                {t('equipment.columns.name')} <span className="text-destructive">*</span>
              </TableHead>
              <TableHead className="min-w-[160px]">{t('equipment.columns.application')}</TableHead>
              <TableHead className="min-w-[100px]">{t('equipment.columns.min')}</TableHead>
              <TableHead className="min-w-[100px]">{t('equipment.columns.max')}</TableHead>
              <TableHead className="w-[60px]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {equipment.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="h-24 text-center">
                  {t('equipment.noEquipment')}
                </TableCell>
              </TableRow>
            ) : (
              equipment.map((item, index) => {
                const rowErrors = getRowErrors(index);
                const isDuplicate = isEquipmentNameDuplicate(
                  item.name,
                  equipment,
                  index
                );
                const hasWarning = hasEquipmentWarning(item);
                const hasInvalidRange =
                  item.thresholdMin !== null &&
                  item.thresholdMax !== null &&
                  item.thresholdMin >= item.thresholdMax;
                const preset = APPLICATION_PRESETS[item.application];
                const isExpanded = expandedRows.has(item._key);
                const itemHasDetails = hasDetails(item);

                return (
                  <Fragment key={item._key}>
                    <TableRow
                      className={cn(
                        rowErrors.length > 0 && 'bg-destructive/5',
                        isDuplicate && 'bg-amber-50',
                        hasInvalidRange && !isDuplicate && 'bg-amber-50',
                        hasWarning && !isDuplicate && !hasInvalidRange && 'bg-amber-50/50'
                      )}
                    >
                      <TableCell className="px-2">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => toggleExpand(item._key)}
                          disabled={disabled}
                          className="h-6 w-6"
                          title={isExpanded ? t('equipment.collapseDetails') : t('equipment.expandDetails')}
                        >
                          {isExpanded ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className={cn('h-4 w-4', itemHasDetails && 'text-blue-500')} />
                          )}
                        </Button>
                      </TableCell>
                      <TableCell className="font-medium text-muted-foreground">
                        {index + 1}
                      </TableCell>
                      <TableCell>
                        <Input
                          value={item.building}
                          onChange={(e) =>
                            handleChange(index, 'building', e.target.value)
                          }
                          disabled={disabled}
                          placeholder={t('equipment.placeholders.building')}
                          className="h-8"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          value={item.room}
                          onChange={(e) =>
                            handleChange(index, 'room', e.target.value)
                          }
                          disabled={disabled}
                          placeholder={t('equipment.placeholders.room')}
                          className="h-8"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          value={item.name}
                          onChange={(e) =>
                            handleChange(index, 'name', e.target.value)
                          }
                          disabled={disabled}
                          placeholder={t('equipment.placeholders.name')}
                          className={cn(
                            'h-8',
                            isDuplicate && 'border-amber-500 focus-visible:ring-amber-500'
                          )}
                        />
                        {isDuplicate && (
                          <p className="mt-1 text-xs text-amber-600">
                            {t('equipment.duplicateName')}
                          </p>
                        )}
                      </TableCell>
                      <TableCell>
                        <Select
                          value={item.application}
                          onValueChange={(value) =>
                            handleChange(index, 'application', value)
                          }
                          disabled={disabled}
                        >
                          <SelectTrigger className="h-8">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="cold_storage">{t('equipment.applications.coldStorage')}</SelectItem>
                            <SelectItem value="refrigerator">{t('equipment.applications.refrigerator')}</SelectItem>
                            <SelectItem value="freezer">{t('equipment.applications.freezer')}</SelectItem>
                            <SelectItem value="ultra_low_freezer">{t('equipment.applications.ultraLowFreezer')}</SelectItem>
                            <SelectItem value="room_temp">{t('equipment.applications.roomTemp')}</SelectItem>
                            <SelectItem value="incubator">{t('equipment.applications.incubator')}</SelectItem>
                            <SelectItem value="custom">{t('equipment.applications.custom')}</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Input
                            type="number"
                            value={item.thresholdMin ?? ''}
                            onChange={(e) =>
                              handleChange(
                                index,
                                'thresholdMin',
                                e.target.value ? parseFloat(e.target.value) : null
                              )
                            }
                            disabled={disabled}
                            placeholder={t('equipment.columns.min')}
                            className="h-8 w-20"
                          />
                          {preset?.unit && (
                            <span className="text-xs text-muted-foreground">
                              {preset.unit}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Input
                            type="number"
                            value={item.thresholdMax ?? ''}
                            onChange={(e) =>
                              handleChange(
                                index,
                                'thresholdMax',
                                e.target.value ? parseFloat(e.target.value) : null
                              )
                            }
                            disabled={disabled}
                            placeholder={t('equipment.columns.max')}
                            className={cn(
                              'h-8 w-20',
                              hasInvalidRange && 'border-amber-500 focus-visible:ring-amber-500'
                            )}
                          />
                          {preset?.unit && (
                            <span className="text-xs text-muted-foreground">
                              {preset.unit}
                            </span>
                          )}
                        </div>
                        {hasInvalidRange && (
                          <p className="mt-1 text-xs text-amber-600">
                            {t('equipment.invalidRange')}
                          </p>
                        )}
                      </TableCell>
                      <TableCell>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemove(index)}
                          disabled={disabled}
                          className="h-8 w-8 text-muted-foreground hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                    {isExpanded && (
                      <TableRow key={`${item._key}-details`} className="bg-muted/30">
                        <TableCell colSpan={9} className="px-6 py-3">
                          <div className="grid grid-cols-3 gap-4">
                            <div>
                              <label className="mb-1 block text-xs font-medium text-muted-foreground">
                                {t('equipment.columns.sku')}
                              </label>
                              <Input
                                value={item.sku}
                                onChange={(e) => handleChange(index, 'sku', e.target.value)}
                                disabled={disabled}
                                placeholder={t('equipment.placeholders.sku')}
                                className="h-8"
                              />
                            </div>
                            <div>
                              <label className="mb-1 block text-xs font-medium text-muted-foreground">
                                {t('equipment.columns.calibrationCertificate')}
                              </label>
                              <Input
                                value={item.calibrationCertificate}
                                onChange={(e) => handleChange(index, 'calibrationCertificate', e.target.value)}
                                disabled={disabled}
                                placeholder={t('equipment.placeholders.calibrationCertificate')}
                                className="h-8"
                              />
                            </div>
                            <div>
                              <label className="mb-1 block text-xs font-medium text-muted-foreground">
                                {t('equipment.columns.installationDate')}
                              </label>
                              <Input
                                value={item.installationDate}
                                onChange={(e) => handleChange(index, 'installationDate', e.target.value)}
                                disabled={disabled}
                                placeholder={t('equipment.placeholders.installationDate')}
                                className="h-8"
                              />
                            </div>
                            <div>
                              <label className="mb-1 block text-xs font-medium text-muted-foreground">
                                {t('equipment.columns.installationCertificate')}
                              </label>
                              <Input
                                value={item.installationCertificate}
                                onChange={(e) => handleChange(index, 'installationCertificate', e.target.value)}
                                disabled={disabled}
                                placeholder={t('equipment.placeholders.installationCertificate')}
                                className="h-8"
                              />
                            </div>
                            <div>
                              <label className="mb-1 block text-xs font-medium text-muted-foreground">
                                {t('equipment.columns.invoiceNumber')}
                              </label>
                              <Input
                                value={item.invoiceNumber}
                                onChange={(e) => handleChange(index, 'invoiceNumber', e.target.value)}
                                disabled={disabled}
                                placeholder={t('equipment.placeholders.invoiceNumber')}
                                className="h-8"
                              />
                            </div>
                            <div>
                              <label className="mb-1 block text-xs font-medium text-muted-foreground">
                                {t('equipment.columns.invoiceDate')}
                              </label>
                              <Input
                                value={item.invoiceDate}
                                onChange={(e) => handleChange(index, 'invoiceDate', e.target.value)}
                                disabled={disabled}
                                placeholder={t('equipment.placeholders.invoiceDate')}
                                className="h-8"
                              />
                            </div>
                            <div className="col-span-3">
                              <label className="mb-1 block text-xs font-medium text-muted-foreground">
                                {t('equipment.columns.comments')}
                              </label>
                              <Input
                                value={item.comments}
                                onChange={(e) => handleChange(index, 'comments', e.target.value)}
                                disabled={disabled}
                                placeholder={t('equipment.placeholders.comments')}
                                className="h-8"
                              />
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
